import { CookieStorage } from 'aws-amplify/utils';
import config from '../config';

const AUTH_KEY_PREFIX = 'CognitoIdentityServiceProvider';
const AUTH_BASE_KEY = `${AUTH_KEY_PREFIX}.${config.Cognito.ClientId}`;

const OAUTH_STATE_KEY = `${AUTH_BASE_KEY}.oauthState`;
const OAUTH_PKCE_KEY = `${AUTH_BASE_KEY}.oauthPKCE`;
const OAUTH_SIGN_IN_KEY = `${AUTH_BASE_KEY}.oauthSignIn`;
const AMPLIFY_INFLIGHT_OAUTH_KEY = `${AUTH_BASE_KEY}.inflightOAuth`;

const OAUTH_REDIRECT_URI_KEY = `${AUTH_BASE_KEY}.oauthRedirectUri`;

const LOCAL_INFLIGHT_KEY = `cogneed.auth.oauth.${config.Cognito.ClientId}.inflight`;
const LOCAL_INFLIGHT_AT_KEY = `cogneed.auth.oauth.${config.Cognito.ClientId}.inflightAt`;
const LOCAL_INFLIGHT_TTL_MS = 10 * 60 * 1000;

// Must match config.Authentication.OpenExternalAuthorizeParam in the Electron main process.
const OPEN_EXTERNAL_PARAM = 'cogneed_open_external';

// Prefix embedded in the OAuth state to identify dual-canal-initiated flows.
// When the external browser receives the Cognito callback, this prefix tells the
// live page to bridge the code back to Electron via the deep-link protocol instead
// of mounting the React app.
export const DUAL_CANAL_STATE_PREFIX = 'dc.';

function formatRedirectBase(rawValue: string): string {
  const trimmed = rawValue.trim();
  const withoutHash = trimmed.split('#')[0] ?? trimmed;
  const withoutSearch = withoutHash.split('?')[0] ?? withoutHash;
  const url = new URL(withoutSearch);
  const result = `${url.origin}${url.pathname}`;
  return result.endsWith('/') ? result.slice(0, -1) : result;
}

function getCognitoRedirectBase(): string {
  return formatRedirectBase(window.location.href);
}

export const SIGN_IN_ERROR_STORAGE_KEY = 'cogneed.auth.signInWithRedirect.failure';

const cognitoKVStore = new CookieStorage({
  domain: config.Auth.Cookies.CookieDomain,
  path: '/',
  expires: 365,
  sameSite: 'none',
  secure: true,
});

type TokenResponse = {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
};

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecodeToString(input: string): string {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (normalized.length % 4)) % 4;
  return atob(normalized + '='.repeat(padLen));
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    return JSON.parse(base64UrlDecodeToString(parts[1]!));
  } catch {
    return null;
  }
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

async function generatePkcePair(): Promise<{ verifier: string; challenge: string }> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const verifier = base64UrlEncode(bytes);
  const challenge = await sha256Base64Url(verifier);
  return { verifier, challenge };
}

function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return DUAL_CANAL_STATE_PREFIX + base64UrlEncode(bytes);
}

function clearLocalInflight() {
  try {
    localStorage.removeItem(LOCAL_INFLIGHT_KEY);
    localStorage.removeItem(LOCAL_INFLIGHT_AT_KEY);
  } catch {
    // no-op
  }
}

function setLocalInflight() {
  try {
    localStorage.setItem(LOCAL_INFLIGHT_KEY, 'true');
    localStorage.setItem(LOCAL_INFLIGHT_AT_KEY, `${Date.now()}`);
  } catch {
    // no-op
  }
}

export function isLocalInflight(): boolean {
  try {
    const inflight = localStorage.getItem(LOCAL_INFLIGHT_KEY) === 'true';
    if (!inflight) return false;
    const at = Number.parseInt(localStorage.getItem(LOCAL_INFLIGHT_AT_KEY) ?? '0', 10);
    if (Number.isFinite(at) && at > 0 && Date.now() - at > LOCAL_INFLIGHT_TTL_MS) {
      clearLocalInflight();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function stripAuthParamsFromUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    window.history.replaceState({}, document.title, url.toString());
  } catch {
    // no-op
  }
}

async function exchangeCodeForTokens(params: { code: string; verifier: string }): Promise<TokenResponse> {
  const tokenUrl = `https://${config.Cognito.Oauth.Domain}/oauth2/token`;
  const storedRedirectUri = localStorage.getItem(OAUTH_REDIRECT_URI_KEY);
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.Cognito.ClientId,
    code: params.code,
    redirect_uri: storedRedirectUri ?? getCognitoRedirectBase(),
    code_verifier: params.verifier,
  });

  const resp = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Token exchange failed (${resp.status}): ${text || resp.statusText}`);
  }

  return resp.json() as Promise<TokenResponse>;
}

async function seedAmplifyTokenStore(tokens: TokenResponse) {
  const accessPayload = decodeJwtPayload(tokens.access_token);
  const idPayload = tokens.id_token ? decodeJwtPayload(tokens.id_token) : null;

  const username = idPayload?.['cognito:username']
    || idPayload?.username
    || accessPayload?.username
    || accessPayload?.['cognito:username']
    || idPayload?.sub
    || accessPayload?.sub;

  if (!username || typeof username !== 'string') {
    throw new Error('Unable to infer username from tokens');
  }

  const accessIatSeconds = typeof accessPayload?.iat === 'number' ? accessPayload.iat : 0;
  const clockDrift = accessIatSeconds > 0 ? accessIatSeconds * 1000 - Date.now() : 0;

  const base = `${AUTH_KEY_PREFIX}.${config.Cognito.ClientId}.${username}`;

  await cognitoKVStore.setItem(`${AUTH_BASE_KEY}.LastAuthUser`, username);
  await cognitoKVStore.setItem(`${base}.accessToken`, tokens.access_token);

  if (tokens.id_token) {
    await cognitoKVStore.setItem(`${base}.idToken`, tokens.id_token);
  } else {
    await cognitoKVStore.removeItem(`${base}.idToken`);
  }

  if (tokens.refresh_token) {
    await cognitoKVStore.setItem(`${base}.refreshToken`, tokens.refresh_token);
  } else {
    await cognitoKVStore.removeItem(`${base}.refreshToken`);
  }

  await cognitoKVStore.setItem(`${base}.clockDrift`, `${clockDrift}`);
  await cognitoKVStore.setItem(`${base}.oauthMetadata`, JSON.stringify({ oauthSignIn: true }));

  try {
    localStorage.setItem(OAUTH_SIGN_IN_KEY, 'true,false');
  } catch {
    // no-op
  }
}

/**
 * Call as early as possible. If the URL includes `?code=...&state=...`, finalizes the OAuth
 * code grant by exchanging the code for tokens and seeding Amplify's token store.
 */
export async function finalizeHostedUiSignInFromUrl(): Promise<void> {
  try {
    localStorage.removeItem(AMPLIFY_INFLIGHT_OAUTH_KEY);
  } catch {
    // no-op
  }

  const params = new URL(window.location.href).searchParams;
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  const errorDescription = params.get('error_description');

  if (error) {
    clearLocalInflight();
    stripAuthParamsFromUrl();
    try {
      sessionStorage.setItem(SIGN_IN_ERROR_STORAGE_KEY, errorDescription ? `${error}: ${errorDescription}` : error);
    } catch {
      // no-op
    }
    return;
  }

  if (!code || !state) {
    return;
  }

  try {
    const expectedState = localStorage.getItem(OAUTH_STATE_KEY);
    const verifier = localStorage.getItem(OAUTH_PKCE_KEY);

    if (!expectedState || expectedState !== state) {
      throw new Error('OAuth state mismatch (stale or missing state)');
    }
    if (!verifier) {
      throw new Error('Missing PKCE verifier (stale or cleared storage)');
    }

    const tokenResponse = await exchangeCodeForTokens({ code, verifier });
    await seedAmplifyTokenStore(tokenResponse);

    try {
      sessionStorage.removeItem(SIGN_IN_ERROR_STORAGE_KEY);
    } catch {
      // no-op
    }
  } catch (err) {
    console.error('Failed to finalize hosted UI sign-in:', err);
    try {
      const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      sessionStorage.setItem(SIGN_IN_ERROR_STORAGE_KEY, message);
    } catch {
      // no-op
    }
  } finally {
    clearLocalInflight();
    try {
      localStorage.removeItem(OAUTH_STATE_KEY);
      localStorage.removeItem(OAUTH_PKCE_KEY);
      localStorage.removeItem(OAUTH_REDIRECT_URI_KEY);
      localStorage.removeItem(AMPLIFY_INFLIGHT_OAUTH_KEY);
    } catch {
      // no-op
    }
    stripAuthParamsFromUrl();
  }
}

/**
 * Starts the Cognito Hosted UI PKCE flow.
 * By default the Electron main process intercepts the navigation and opens it
 * externally, keeping the home window on the live app. Pass `{ inElectron: true }`
 * to open the Cognito page inside the Electron window instead.
 */
export async function startHostedUiSignIn(options?: { inElectron?: boolean }): Promise<void> {
  try {
    sessionStorage.removeItem(SIGN_IN_ERROR_STORAGE_KEY);
  } catch {
    // no-op
  }
  clearLocalInflight();

  const { verifier, challenge } = await generatePkcePair();
  const state = generateState();

  const redirectUri = getCognitoRedirectBase();
  localStorage.setItem(OAUTH_PKCE_KEY, verifier);
  localStorage.setItem(OAUTH_STATE_KEY, state);
  localStorage.setItem(OAUTH_REDIRECT_URI_KEY, redirectUri);
  localStorage.setItem(OAUTH_SIGN_IN_KEY, 'true,false');
  localStorage.removeItem(AMPLIFY_INFLIGHT_OAUTH_KEY);

  setLocalInflight();

  const authorizeUrl = new URL(`https://${config.Cognito.Oauth.Domain}/oauth2/authorize`);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('client_id', config.Cognito.ClientId);
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('scope', config.Cognito.Oauth.Scopes.join(' '));
  authorizeUrl.searchParams.set('state', state);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');
  authorizeUrl.searchParams.set('code_challenge', challenge);
  if (!options?.inElectron) {
    authorizeUrl.searchParams.set(OPEN_EXTERNAL_PARAM, 'true');
  }
  window.location.assign(authorizeUrl.toString());
}

export async function clearHostedUiStorage(): Promise<void> {
  clearLocalInflight();

  try {
    await window.utilsAPI?.clearHostedUiBrowserStorage?.();
  } catch {
    // no-op — not available outside Electron or if preload is absent
  }

  try {
    localStorage.removeItem(OAUTH_STATE_KEY);
    localStorage.removeItem(OAUTH_PKCE_KEY);
    localStorage.removeItem(OAUTH_REDIRECT_URI_KEY);
    localStorage.removeItem(OAUTH_SIGN_IN_KEY);
    localStorage.removeItem(AMPLIFY_INFLIGHT_OAUTH_KEY);
  } catch {
    // no-op
  }

  const lastAuthUserKey = `${AUTH_BASE_KEY}.LastAuthUser`;
  let username: string | null = null;
  try {
    username = await cognitoKVStore.getItem(lastAuthUserKey);
  } catch {
    username = null;
  }

  if (username) {
    const base = `${AUTH_KEY_PREFIX}.${config.Cognito.ClientId}.${username}`;
    await Promise.allSettled([
      cognitoKVStore.removeItem(`${base}.accessToken`),
      cognitoKVStore.removeItem(`${base}.idToken`),
      cognitoKVStore.removeItem(`${base}.refreshToken`),
      cognitoKVStore.removeItem(`${base}.clockDrift`),
      cognitoKVStore.removeItem(`${base}.oauthMetadata`),
    ]);
  }

  await Promise.allSettled([
    cognitoKVStore.removeItem(lastAuthUserKey),
  ]);
}
